import React, { useEffect, useState } from 'react';
import './AccountButton.css';
import { useAuth } from '../utils/AuthContext';
import { useGame } from '../utils/GameContext'; 
import { useAudio } from '../utils/AudioContext'; // Import AudioContext
import { playUiHoverSound, playUiClickSound } from '../utils/sounds'; // Import sounds
import { User, LogIn, Coins } from 'lucide-react'; 
import { AVATARS } from './AccountMenu'; 
import { ref, get } from 'firebase/database';
import { db } from '../../firebase';

function AccountButton({ onClick }) {
    const { currentUser, userData } = useAuth();
    const { gameState } = useGame(); 
    const { sfxMuted } = useAudio(); // Get audio state

    const [rankDisplay, setRankDisplay] = useState('---');
    
    // State to toggle between Profile info and Money
    const [showBalance, setShowBalance] = useState(false);
    
    // Guest Incentive Logic
    const [guestMsgIndex, setGuestMsgIndex] = useState(0);
    const GUEST_MESSAGES = [
        "Save Progress",
        "Rank Up",
        "Play w/ Friends"
    ];

    // Toggle Interval
    useEffect(() => {
        const interval = setInterval(() => {
            setShowBalance(prev => !prev);
        }, 3000); 

        return () => clearInterval(interval);
    }, []);

    // Cycle guest messages
    useEffect(() => {
        if (!currentUser && !showBalance) {
            const t = setTimeout(() => {
                 setGuestMsgIndex(prev => (prev + 1) % GUEST_MESSAGES.length);
            }, 500); 
            return () => clearTimeout(t);
        }
    }, [showBalance, currentUser]);

    // Calculate Rank
    useEffect(() => {
        const fetchRank = async () => {
            if (!currentUser) {
                setRankDisplay('');
                return;
            }
            const myScore = userData?.highscore?.alltime?.score || 0;
            if (myScore === 0) {
                setRankDisplay('N/A'); 
                return;
            }
            try {
                const lbRef = ref(db, 'leaderboards/alltime');
                const snapshot = await get(lbRef);
                if (snapshot.exists()) {
                    const data = snapshot.val();
                    const sorted = Object.keys(data)
                        .map(key => ({ uid: key, ...data[key] }))
                        .sort((a, b) => b.score - a.score);
                    const myRank = sorted.findIndex(u => u.uid === currentUser.uid) + 1;
                    setRankDisplay(myRank > 0 ? `#${myRank}` : 'N/A'); 
                } else {
                    setRankDisplay('N/A'); 
                }
            } catch (err) {
                console.error("Error fetching rank for button", err);
                setRankDisplay('---');
            }
        };
        fetchRank();
    }, [currentUser, userData]); 

    const currentAvatarObj = currentUser && userData 
        ? AVATARS.find(a => a.id === userData.avatarId)
        : null;

    const CurrentAvatarIcon = currentAvatarObj?.icon || <User />;
    const avatarColor = currentAvatarObj?.color || '#3b82f6';

    const isHidden = gameState !== 'ready';

    const handleClick = () => {
        playUiClickSound(sfxMuted);
        onClick();
    };

    return (
        <button 
            className={`account-pill-btn ${isHidden ? 'hidden' : ''}`} 
            onClick={handleClick}
            onMouseEnter={() => playUiHoverSound(sfxMuted)}
            disabled={isHidden}
        >
            <div 
                className="pill-avatar-circle"
                style={{
                    borderColor: currentUser ? avatarColor : '#ef4444',
                    boxShadow: `0 0 10px ${currentUser ? avatarColor : '#ef4444'}80`,
                    color: currentUser ? avatarColor : '#ef4444',
                    position: 'relative'
                }}
            >
                {currentUser ? CurrentAvatarIcon : <LogIn size={18} />}
                
                {!currentUser && (
                    <div style={{
                        position: 'absolute',
                        top: '-2px',
                        right: '-2px',
                        width: '8px',
                        height: '8px',
                        backgroundColor: '#ef4444',
                        borderRadius: '50%',
                        border: '2px solid #0f172a'
                    }}/>
                )}
            </div>
            
            <div className="pill-content-wrapper">
                <div className={`pill-slider ${showBalance ? 'show-balance' : ''}`}>
                    
                    <div className="pill-slide-item">
                        <span className="pill-label">
                            {currentUser ? (userData?.username || 'Player') : 'Sign In'}
                        </span>
                        {currentUser ? (
                            <span className="pill-sublabel">
                                Rank {rankDisplay}
                            </span>
                        ) : (
                            <span className="pill-sublabel" style={{color: '#94a3b8'}}>
                                Guest
                            </span>
                        )}
                    </div>

                    <div className="pill-slide-item balance-view">
                        {currentUser ? (
                            <>
                                <div className="money-icon-row">
                                    <Coins size={12} className="c-yellow" style={{marginRight: '4px'}}/>
                                    <span className="pill-sublabel">Balance</span>
                                </div>
                                <span className="pill-label money-label">
                                    {userData?.money || 0}
                                </span>
                            </>
                        ) : (
                            <div style={{display:'flex', flexDirection:'column', alignItems:'center', width:'100%'}}>
                                <span className="pill-label" style={{fontSize: '0.75rem', color:'#fbbf24', lineHeight: 1.2}}>
                                    Why Join?
                                </span>
                                <span className="pill-sublabel" style={{color: '#fff', fontSize:'0.75rem'}}>
                                    {GUEST_MESSAGES[guestMsgIndex]}
                                </span>
                            </div>
                        )}
                    </div>

                </div>
            </div>
        </button>
    );
}

export default AccountButton;